/* Every file of a skill: the folder tree on the left, the open file on the right —
   stacked on a phone. SKILL.md opens first. */
import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, File, FileCode2, FileText, Folder, Image } from "lucide-react";

import { buildTree, formatBytes } from "../catalog";
import FileViewer from "./FileViewer";

import type { FileNode } from "../catalog";
import type { SkillDetail } from "@/shared/types";

/* A hint from the name alone; the viewer decides from the bytes. */
function iconFor(path: string) {
  if (/\.(md|mdx|markdown)$/i.test(path)) return FileText;
  if (/\.(png|jpe?g|gif|webp|svg)$/i.test(path)) return Image;
  if (/\.(pdf|zip|gz|ttf|otf|woff2?)$/i.test(path)) return File;
  return FileCode2;
}

function Tree({ nodes, depth, openPath, closed, onOpen, onToggle }: {
  nodes: FileNode[];
  depth: number;
  openPath: string | null;
  closed: Set<string>;
  onOpen: (path: string) => void;
  onToggle: (path: string) => void;
}) {
  return (
    <>
      {nodes.map((node) => {
        const pad = { paddingLeft: 10 + depth * 14 };
        if (node.children) {
          const isClosed = closed.has(node.path);
          return (
            <div key={node.path}>
              <button
                onClick={() => onToggle(node.path)}
                aria-expanded={!isClosed}
                className="press w-full flex items-center gap-1.5 pr-2 py-[5px] text-left text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
                style={pad}
              >
                {isClosed
                  ? <ChevronRight className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={2} />
                  : <ChevronDown className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={2} />}
                <Folder className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
                <span className="truncate">{node.name}</span>
              </button>
              {!isClosed && (
                <Tree nodes={node.children} depth={depth + 1} openPath={openPath} closed={closed} onOpen={onOpen} onToggle={onToggle} />
              )}
            </div>
          );
        }
        const Icon = iconFor(node.path);
        const active = node.path === openPath;
        return (
          <button
            key={node.path}
            onClick={() => onOpen(node.path)}
            aria-current={active ? "true" : undefined}
            title={node.path}
            className={`press w-full flex items-center gap-1.5 pr-2 py-[5px] text-left text-[12.5px] ${
              active
                ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            }`}
            style={{ paddingLeft: pad.paddingLeft + 18 }}
          >
            <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            <span className="truncate">{node.name}</span>
          </button>
        );
      })}
    </>
  );
}

export default function FileBrowser({ skill }: { skill: SkillDetail }) {
  const tree = useMemo(() => buildTree(skill.files), [skill.files]);
  const first = skill.files.find((f) => f.path === "SKILL.md") ?? skill.files[0];
  const [openPath, setOpenPath] = useState<string | null>(first?.path ?? null);
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const toggle = (path: string) =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const open = skill.files.find((f) => f.path === openPath) ?? null;

  if (!skill.files.length) {
    return <div className="py-10 text-center text-[13px] text-[var(--text-tertiary)]">This skill has no files stored.</div>;
  }

  return (
    <div className="space-y-2">
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden flex flex-col md:flex-row">
        <nav aria-label="Files" className="md:w-[240px] shrink-0 border-b md:border-b-0 md:border-r hairline overflow-y-auto max-h-[240px] md:max-h-[560px] py-1.5">
          <Tree nodes={tree} depth={0} openPath={openPath} closed={closed} onOpen={setOpenPath} onToggle={toggle} />
        </nav>
        <div className="flex-1 min-w-0">
          {open ? <FileViewer key={open.path} skill={skill} file={open} /> : null}
        </div>
      </div>
      {(skill.skipped_files.length > 0 || skill.files_past_limit > 0) && (
        <p className="text-[12px] text-[var(--text-tertiary)] leading-[1.6] m-0">
          {skill.skipped_files.length > 0 &&
            `Not stored, over 5 MB: ${skill.skipped_files.map((f) => `${f.path} (${formatBytes(f.size)})`).join(", ")}. `}
          {skill.files_past_limit > 0 && `${skill.files_past_limit} more files past the first 500 aren't stored. `}
          The full folder is on GitHub.
        </p>
      )}
    </div>
  );
}
